import React, {useContext, useEffect, useState} from 'react';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';

import {useStyles} from "./ContentCreatorDashboardSection.css";
import {AppbarContentContext} from "../../../../../commons/routing/appbar/AppbarContentProvider";
import {FormattedMessage, useIntl} from 'react-intl';
import useFetchContentCreatorDashboardData from "./useFetchContentCreatorDashboardData";
import LoadingProgressIndication from "../../../../../commons/elements/progress/loading/LoadingProgressIndication";
import {IconButton, MenuItem, Select, Typography} from '@mui/material';
import FormControl from "@mui/material/FormControl";
import noDashboardData from 'assets/noDashboardData.svg';
import {FirebaseAuthContext} from "../../../../../commons/firebase/FirebaseAuthProvider";
import {DateRangeSharp, Female, Male, VideoLibrary} from "@mui/icons-material";
import StatisticsDateRangePicker from "../../../statistics/utils/StatisticsDateRangePicker";
import {useConfirm} from "material-ui-confirm";
import {subDays} from 'date-fns';
import {NavLink} from "react-router-dom";
import BasicChart from "../../../statistics/chart/BasicChart";
import ValueIndicator from "../../panels/valueIndication/ValueIndicator";
import ValueIndicationList from "../../panels/valueIndicationList/ValueIndicationList";

export default function ContentCreatorDashboardSection() {
    const classes = useStyles();

    // Setting the current appbar content
    const {setCurrentAppbarContent} = useContext(AppbarContentContext)

    // Confirmation dialog for selecting the time range
    const confirm = useConfirm();

    // Get permission-based limited association structure to fill the association filter dropdown
    const {allowedAssociationStructure} = useContext(FirebaseAuthContext);

    // Internationalization for simple strings
    const intl = useIntl();

    // Hook for fetching the data required for rendering the statistics
    const {fetchContentCreatorDashboardData} = useFetchContentCreatorDashboardData()

    // Filter
    const [associationFilter, setAssociationFilter] = useState(Object.keys(allowedAssociationStructure)[0] || '')
    const [fromToDateRange, setFromToDateRange] = React.useState([subDays(new Date(), 365), new Date()])

    // Raw dashboard data object as basis to render charts and numbers
    const [dashboardData, setDashboardData] = useState({})

    // Loading indication - set to false in case all charts are ready
    const [loadingStatistics, setLoadingStatistics] = useState(true)


    // Setting the appbar content including the filters
    useEffect(() => {
        setCurrentAppbarContent({
            header: <FormattedMessage id={'app.dashboard.overview.appbar.header'}
                                      defaultMessage={'Dashboard'}/>,
            description: <FormattedMessage id={'app.dashboard.overview.appbar.description'}
                                           defaultMessage={'All important KPIs at one glance...'}/>,
            actions: <>
                {getFilterSelection()}
            </>
        })
    }, [associationFilter, fromToDateRange, loadingStatistics])

    // Automatically calculate statistics on filter change
    useEffect(() => {
        reloadDashboardData()
    }, [associationFilter, fromToDateRange])

    // Query statistical data and fill the chart canvas accordingly
    const reloadDashboardData = async () => {
        setLoadingStatistics(true)

        // Load the basic statistical data as a basis for feeding the charts in the dashboard
        const dashboardData = await fetchContentCreatorDashboardData(associationFilter, fromToDateRange[0], fromToDateRange[1])

        // Set the dashboard data state, so that it can be used by the charts - go sure that the dashboard data is never set to null to avoid object access problems
        setDashboardData(dashboardData || {})

        setLoadingStatistics(false)
    }

    const getFilterSelection = () => {

        // Hide dropdown in case of loading
        if (loadingStatistics) {
            return <></>
        }

        // Filter row
        return <div className={classes.filterRow}>

            {/* Association */}
            <FormControl className={classes.formControl}>
                <Select id="reg-course-association-input"
                        style={{color: "white", borderRadius: 30, borderColor: "grey"}}
                        variant="outlined"
                        value={associationFilter || ''}
                        onChange={(event) => setAssociationFilter(event.target.value)}>

                    {Object.keys(allowedAssociationStructure).map((association) =>
                        <MenuItem key={association} value={association}>
                            {intl.formatMessage({
                                id: `app.ids.association.${association}`
                            })}
                        </MenuItem>
                    )}
                </Select>
            </FormControl>

            {/* Date range */}
            <div style={{marginLeft: 10}}>
                <b>{intl.formatDate(fromToDateRange[0]) + ' - ' + intl.formatDate(fromToDateRange[1])}</b>
                <IconButton onClick={handlePickDate} size="large">
                    <DateRangeSharp/>
                </IconButton>
            </div>
        </div>
    }

    // Selection of time range
    const handlePickDate = () => {
        let selectedFromDate
        let selectedToDate

        confirm({
            title: intl.formatMessage({
                id: 'app.dashboard.dialog.dateRange.selection.title',
                defaultMessage: 'Choose time range...'
            }),
            content: <StatisticsDateRangePicker
                startDate={fromToDateRange[0]}
                endDate={fromToDateRange[1]}
                onPeriodChanged={(startDate, endDate) => {
                    selectedFromDate = startDate
                    selectedToDate = endDate
                }
                }/>
        }).then(() => {

            // IMPORTANT: When selecting nothing, but clicking simply on OK, selectedStartDate and selectedEndDate is undefined
            // IMPORTANT: the timestamps are always at "start date 00:00:00 UTC" - "end date 23:59:59 UTC"
            if (selectedFromDate && selectedToDate) {
                setFromToDateRange([selectedFromDate, selectedToDate])
            }
        }).catch(() => null)
    }

    return (
        <div className={classes.root}>

            {/* Only hide the loading indication, so that the canvas-elements are always available in the DOM */}
            <div hidden={!loadingStatistics}>
                <LoadingProgressIndication text={intl.formatMessage({
                    id: 'app.dashboard.loading',
                    defaultMessage: 'Loading Dashboard...'
                })}/>
            </div>

            {/* Hide complete section while loading and in case no statistics are generated yet */}
            <Grid container spacing={2}
                  sx={(loadingStatistics || dashboardData.noDataYet) && {display: 'none'}}>

                {/* Total numbers of created scored scenes grouped by discipline (including an "All" discipline) */}
                <Grid item xs={12}>
                    <ValueIndicationList
                        headerText={intl.formatMessage({
                            id: 'app.dashboard.overview.createdScoredScenes.header',
                            defaultMessage: 'Scenes'
                        })}
                        headerIcon={<VideoLibrary fontSize="large"/>}
                        labelValueList={dashboardData.listCreatedScoredScenesGroupedByDiscipline}
                        secondaryValueSelectors={['countMale', 'countFemale']}
                        secondaryIcons={[<Male/>, <Female/>]}
                        secondaryColors={['#1C92D8', '#F06C6B']}/>
                </Grid>

                {/* Chart of created scored scenes grouped by discipline */}
                <Grid item xs={9}>
                    <BasicChart chartId="chartCreatedScoredScenesByDisciplines"
                                chartData={dashboardData.chartCreatedScoredScenesByDiscipline}/>
                </Grid>

                {/* Challenged decisions and rejected scored scenes */}
                <Grid item xs={3}>
                    <Paper className={classes.groupPaper50}>

                        {/* Header */}
                        <div className={classes.smallPanelHeader}>
                            <NavLink to={`/admin/challengedDecisions`}>
                                {intl.formatMessage({
                                    id: 'app.dashboard.overview.challengedDecisions.header',
                                    defaultMessage: 'Open Challenges'
                                })}
                            </NavLink>
                        </div>

                        {/* Number of total open challenged decisions of the selected association, where the signed-in user is the owner of the scored scene */}
                        <ValueIndicator
                            highlighted={true}
                            size="big"
                            header={intl.formatMessage({
                                id: 'app.dashboard.overview.challengedDecisions.metric.open.assignedToYou',
                                defaultMessage: 'Assigned to you'
                            })}
                            value={dashboardData.numberOpenChallengedDecisionsResolvable}/>

                        {/* Number of total open challenged decisions of the selected association */}
                        <ValueIndicator
                            size="big"
                            header={intl.formatMessage({
                                id: 'app.dashboard.overview.challengedDecisions.metric.open.total',
                                defaultMessage: 'Total'
                            })}
                            value={dashboardData.numberOpenChallengedDecisionsTotal}/>

                    </Paper>

                    {/* Rejected scored scenes (all rejected vs. rejected resolvable by signed-in user) */}
                    <Paper className={classes.groupPaper50}>

                        {/* Header */}
                        <div className={classes.smallPanelHeader}>
                            <NavLink to={`/admin/videos`}>
                                {intl.formatMessage({
                                    id: 'app.dashboard.overview.rejectedScoredScenes.header',
                                    defaultMessage: 'Rejected Scenes'
                                })}
                            </NavLink>
                        </div>

                        {/* Number of total open challenged decisions of the selected association, where the signed-in user is the owner of the scored scene */}
                        <ValueIndicator
                            highlighted={true}
                            size="big"
                            header={intl.formatMessage({
                                id: 'app.dashboard.overview.rejectedScoredScenes.metric.open.assignedToYou',
                                defaultMessage: 'Assigned to you'
                            })}
                            value={dashboardData.numberRejectedScoredScenesResolvable}/>

                        {/* Number of total open challenged decisions of the selected association */}
                        <ValueIndicator
                            size="big"
                            header={intl.formatMessage({
                                id: 'app.dashboard.overview.rejectedScoredScenes.metric.open.total',
                                defaultMessage: 'Total'
                            })}
                            value={dashboardData.numberRejectedScoredScenesTotal}/>

                    </Paper>

                </Grid>

            </Grid>

            {/* In case no statistical data has been generated yet - show indication with nice cartoon image */}
            <div hidden={loadingStatistics || !dashboardData.noDataYet}>
                <Typography component="h1" variant="h4" textAlign="center">
                    {intl.formatMessage({
                        id: 'app.dashboard.overview.noData.header',
                        defaultMessage: 'Missing data to render dashboard...'
                    })}
                </Typography>
                <img className={classes.image} src={noDashboardData} alt='No dashboard data'/>
                <Typography component="h1" variant="h5" textAlign="center">
                    {intl.formatMessage({
                        id: 'app.dashboard.overview.noData.description',
                        defaultMessage: 'No activity for the requested filter criteria.'
                    })}
                </Typography>
            </div>
        </div>
    );
}
